import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TableRow, Dropdown } from 'semantic-ui-react';
import { COLORS, Row } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';

import { Table, Filters, TableCell } from 'comps';
import TransferStatusCards from 'comps/trasfer-status-cards';
import {
  STUDENT_TRANSFER_REASONS_OPTIONS,
  TRANSFER_STATUS_FILTER_OPTIONS,
  SDT_DRAWER_CARD,
  TRANSFER_REASONS,
  REGULAR_SLOTS_FILTER_OPTION,
  CLASS_COMPLETED_FILTER_OPTION,
} from 'constants/student-transfer';
import Search from 'modules/list/new-search-bar';
import {
  convertEpochDateToSTSlotTime,
  getDateOfRequest,
  getFilteredStudentTransferInfo,
  semanticDropdownDataFormat,
} from 'utils/student-transfer';

import {
  EditSlotIcon,
  EditSlotWrapper,
  HeaderCard,
  InfoIcon,
  SlotsWrapper,
  StyledTableCell,
  TableContainer,
  TransferTableRoot,
} from './styles/transfer-info';
import { bindActionCreators } from 'redux';
import { openModal } from 'actions/modal';
import { connect } from 'react-redux';

const TransferInfoTable = ({
  openDrawer,
  studentTransferAPIData,
  refetchStudentTransferList,
  fetchingStudentList,
  actions,
}) => {
  const [studentTransferInfo, setStudentTransferInfo] = useState([]);
  const initialStudentTransferInfoRef = useRef();
  const [showLoader, setShowLoader] = useState(false);
  const [filterOptionsSelected, setFilterOptionsSelected] = useState({
    reasonFilter: [],
    statusFilter: [],
    slotsFilter: [],
    classCompletedFilter: [],
    searchKey: '',
  });

  const loadAndEmptyTransferList = () => {
    setShowLoader(true);
    setStudentTransferInfo(null);
  };

  const onReasonFilter = (_, data) => {
    const { value } = data || {};

    loadAndEmptyTransferList();
    setFilterOptionsSelected(prev => ({ ...prev, reasonFilter: value }));
  };

  const onTransferStatusFilter = (_, data) => {
    const { value } = data || {};

    loadAndEmptyTransferList();
    setFilterOptionsSelected(prev => ({ ...prev, statusFilter: value }));
  };

  const onSlotsFilter = (_, data) => {
    const { value } = data || {};

    loadAndEmptyTransferList();
    setFilterOptionsSelected(prev => ({ ...prev, slotsFilter: value }));
  };

  const onClassCompletedFilter = (_, data) => {
    const { value } = data || {};

    loadAndEmptyTransferList();
    setFilterOptionsSelected(prev => ({ ...prev, classCompletedFilter: value }));
  };

  const onSearch = searchVal => {
    if (searchVal.length > 3) {
      setShowLoader(true);
      setFilterOptionsSelected(prev => ({ ...prev, searchKey: searchVal }));
    } else if (!searchVal) {
      setShowLoader(true);
      setFilterOptionsSelected(prev => ({ ...prev, searchKey: '' }));
    }
  };

  const assigningSingleStudent = useCallback(() => {
    loadAndEmptyTransferList();

    if (typeof refetchStudentTransferList === 'function') {
      refetchStudentTransferList();
    }
  }, [refetchStudentTransferList]);

  const handleOnReasonUpdate = useCallback(
    ({ reason, studentDirectTransferId }) => {
      actions.openModal(
        'student-transfer-reason-update',
        {
          blocking: false,
          transparent: false,
          size: 'custom',
          width: '512px',
          height: '492px',
          radius: '16px',
          modalZIndex: 1000,
          backdropFilter: true,
          backdropFilterData: {
            blurValue: 'blur(20px)',
            bgColor: COLORS.COLA_LIGHTER,
          },
          closeIconAttributes: { placeCloseIcon: true },
        },
        {
          transferReason: reason,
          studentDirectTransferId,
          refreshData: assigningSingleStudent,
        },
      );
    },
    [actions, assigningSingleStudent],
  );

  useEffect(() => {
    const newStudentsInfo = getFilteredStudentTransferInfo({
      studentList: initialStudentTransferInfoRef.current,
      filterOptions: filterOptionsSelected,
    });

    if (newStudentsInfo.length) {
      setStudentTransferInfo(newStudentsInfo);
    }

    setShowLoader(false);
  }, [filterOptionsSelected]);

  useEffect(() => {
    initialStudentTransferInfoRef.current = studentTransferAPIData;
    setStudentTransferInfo(studentTransferAPIData);
    setShowLoader(false);
  }, [studentTransferAPIData]);

  const tableColumnData = [
    {
      title: 'Date of request',
    },
    {
      title: 'Student name',
    },
    { title: 'Previous teacher name' },
    {
      title: 'Reason for transfer',
      showFilters: true,
      filterApplied: filterOptionsSelected.reasonFilter?.length,
      filterComponent: (
        <Dropdown
          clearable
          fluid
          multiple
          search
          selection
          value={filterOptionsSelected.reasonFilter}
          onChange={onReasonFilter}
          options={semanticDropdownDataFormat(STUDENT_TRANSFER_REASONS_OPTIONS)}
        />
      ),
    },
    {
      title: 'Transfer Status',
      showFilters: true,
      filterApplied: filterOptionsSelected.statusFilter?.length,
      filterComponent: (
        <Dropdown
          clearable
          fluid
          multiple
          search
          selection
          value={filterOptionsSelected.statusFilter}
          onChange={onTransferStatusFilter}
          options={semanticDropdownDataFormat(TRANSFER_STATUS_FILTER_OPTIONS)}
        />
      ),
    },
    {
      title: 'Regular slots',
      showFilters: true,
      filterApplied: filterOptionsSelected.slotsFilter?.length,
      filterComponent: (
        <Dropdown
          clearable
          fluid
          multiple
          search
          selection
          value={filterOptionsSelected.slotsFilter}
          onChange={onSlotsFilter}
          options={semanticDropdownDataFormat(REGULAR_SLOTS_FILTER_OPTION)}
        />
      ),
    },
    {
      title: 'Class completed',
      showFilters: true,
      filterApplied: filterOptionsSelected.classCompletedFilter?.length,
      filterComponent: (
        <Dropdown
          clearable
          fluid
          multiple
          search
          selection
          value={filterOptionsSelected.classCompletedFilter}
          onChange={onClassCompletedFilter}
          options={semanticDropdownDataFormat(CLASS_COMPLETED_FILTER_OPTION)}
        />
      ),
    },
  ];

  const handleDrawerOpening = drawerData => {
    const { cardType, studentName } = drawerData || {};
    const drawerInfo = SDT_DRAWER_CARD[cardType];

    if (!drawerInfo || !studentName) return null;

    openDrawer({ ...drawerInfo, props: { ...drawerData } });
  };

  return (
    <TransferTableRoot>
      <Row alignItems="center" justifyContent="space-between">
        <HeaderCard nowrap alignItems="baseline">
          <Text variant="h6">Student Transfer Info</Text>
          <Text variant="body3">(Only Active or Suspended students)</Text>
        </HeaderCard>
        <Search showNewSearchBar={true} placeholder="Search" onSearch={onSearch} />
      </Row>

      <TableContainer>
        <Filters filters={[]} handleFilterChange={() => {}} />
        <Table
          headerCell={tableColumnData}
          dataLength={studentTransferInfo?.length || 0}
          showInfo={true}
          showLoader={fetchingStudentList || showLoader}
          scrollBodyHeight="580px"
          noDataMessage="There's no information to display. Initiate transfer to populate the list of students."
        >
          {studentTransferInfo?.map(studentData => {
            const {
              student_id: studentId,
              student_course: studentCourseInfo,
              reason,
              status: transferStatus,
              created_on: completedOn,
              teacher_name: teacherName,
              new_teacher_name: newTeacherName,
              candidate_teacher_name: candidateTeacherName,
              old_teacher_id: oldTeacherId,
              slot_details: regularSlots,
              class_completed_count: classCompletedCount,
              preferred_teacher_id: preferredTeacherId,
              id: studentDirectTransferId,
            } = studentData || {};
            const {
              student_name: studentName,
              grade,
              region,
              classes_balance: classBalance,
              tenure_end_date: tenureEndDate,
              first_class_taken_on: firstClassTakenOn,
              second_class_taken_on: secondClassTakenOn,
              course_stream: courseStream,
            } = studentCourseInfo || {};

            const slotTime = convertEpochDateToSTSlotTime(regularSlots);

            const dateOfRequest = getDateOfRequest(completedOn);
            const reasonForTransfer = TRANSFER_REASONS[reason];

            const drawerData = {
              cardType: transferStatus,
              studentName,
              grade,
              region,
              classBalance,
              tenureEndDate,
              reasonForTransfer: reason,
              studentId: studentId,
              assigningSingleStudent,
              teacherName,
              regularSlots,
              newTeacherName,
              firstClassTakenOn,
              secondClassTakenOn,
              candidateTeacherName,
              courseStream,
              preferredTeacherId,
              slotTime,
            };

            return (
              <TableRow key={studentId}>
                <TableCell className="center-aligned-flex-element">
                  <InfoIcon onClick={() => handleDrawerOpening(drawerData)} />
                </TableCell>
                <StyledTableCell width={2}>{dateOfRequest || '-'}</StyledTableCell>
                <StyledTableCell
                  width={2}
                  onClick={() => {
                    if (!studentId) return () => {};
                    return window.open(`/students/${studentId}`, '_blank');
                  }}
                >
                  {studentName || '-'}
                </StyledTableCell>
                <StyledTableCell
                  width={2}
                  onClick={() => {
                    if (!oldTeacherId) return () => {};
                    return window.open(`/users/teachers/${oldTeacherId}`, '_blank');
                  }}
                >
                  {teacherName || '-'}
                </StyledTableCell>
                <TableCell>
                  <SlotsWrapper justifyContent="space-between">
                    {reasonForTransfer || '-'}
                    <EditSlotWrapper alignItems="center">
                      <EditSlotIcon
                        onClick={() =>
                          handleOnReasonUpdate({
                            reason,
                            studentDirectTransferId,
                          })
                        }
                      />
                    </EditSlotWrapper>
                  </SlotsWrapper>
                </TableCell>
                <TableCell>
                  <TransferStatusCards cardType={transferStatus} />
                </TableCell>
                <TableCell>
                  <SlotsWrapper justifyContent="space-between">
                    {slotTime || <span>-</span>}
                    <EditSlotWrapper alignItems="center">
                      <EditSlotIcon
                        onClick={() => {
                          if (slotTime) {
                            handleDrawerOpening({
                              ...drawerData,
                              cardType: 'EDIT_STUDENT_SLOT',
                            });
                          } else {
                            handleDrawerOpening(drawerData);
                          }
                        }}
                      />
                    </EditSlotWrapper>
                  </SlotsWrapper>
                </TableCell>
                <TableCell>
                  {classCompletedCount >= 0 ? classCompletedCount : '-'}
                </TableCell>
              </TableRow>
            );
          })}
        </Table>
      </TableContainer>
    </TransferTableRoot>
  );
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ openModal }, dispatch),
});

export default connect(null, mapDispatchToProps)(TransferInfoTable);
